import { format } from 'date-fns';
import type { Cliente, CreateReservaRequest } from '../types';
import { clientesApi } from '../services/api';
import { toLocalDateTimeInput } from './mesaEstado';

export type ReservaFormErrors = Partial<Record<keyof CreateReservaRequest, string>>;

export const MAX_PESSOAS_RESERVA = 20;

export function validateReservaForm(formData: CreateReservaRequest): ReservaFormErrors {
  const errors: ReservaFormErrors = {};
  if (!formData.dataDaReserva) errors.dataDaReserva = 'Data e hora são obrigatórias';
  if (!formData.numeroPessoas || formData.numeroPessoas < 1)
    errors.numeroPessoas = 'Número de pessoas deve ser pelo menos 1';
  if (formData.numeroPessoas > MAX_PESSOAS_RESERVA)
    errors.numeroPessoas = `Máximo ${MAX_PESSOAS_RESERVA} pessoas por reserva`;
  if (!formData.clienteId && !formData.nomeCliente?.trim())
    errors.nomeCliente = 'Nome do cliente é obrigatório';
  return errors;
}

export function buildInitialReservaForm(data: Date, mesaId?: number, capacidade?: number): CreateReservaRequest {
  const dataStr = format(data, 'yyyy-MM-dd');
  const horaStr = format(new Date(), 'HH:mm');
  return {
    dataDaReserva: `${dataStr}T${horaStr}`,
    numeroPessoas: capacidade ?? 2,
    contatoReserva: '',
    notasReserva: '',
    nomeCliente: '',
    clienteId: undefined,
    mesaId,
    valor: 0,
  };
}

export function buildReservaFormFromNow(): CreateReservaRequest {
  return {
    dataDaReserva: toLocalDateTimeInput(new Date()),
    numeroPessoas: 2,
    contatoReserva: '',
    notasReserva: '',
    nomeCliente: '',
    clienteId: undefined,
    mesaId: undefined,
  };
}

/**
 * Garante que existe ficha de cliente para a reserva.
 * - Se clienteId existe e nome/telefone mudaram, propaga para a ficha.
 * - Se não há clienteId mas há nome, cria ficha nova.
 * Devolve o clienteId resolvido (ou undefined se não foi possível).
 * Nunca lança: falhas de cliente não bloqueiam a reserva.
 */
export async function ensureClienteForReserva(args: {
  formData: CreateReservaRequest;
  clientes: Cliente[];
  onClientesChanged?: () => void;
}): Promise<number | undefined> {
  const { formData, clientes, onClientesChanged } = args;
  let clienteId = formData.clienteId;

  if (clienteId) {
    const cli = clientes.find((c) => c.id === clienteId);
    const nomeNovo = formData.nomeCliente?.trim();
    const telNovo = formData.contatoReserva?.trim();
    if (cli && (nomeNovo !== cli.nome || (telNovo || '') !== (cli.telefone || ''))) {
      try {
        await clientesApi.update(cli.id, {
          nome: nomeNovo || cli.nome,
          telefone: telNovo || undefined,
          versao: cli.versao || 1,
        });
        onClientesChanged?.();
      } catch {
        // não bloqueia a reserva
      }
    }
    return clienteId;
  }

  if (formData.nomeCliente?.trim()) {
    try {
      const novo = await clientesApi.create({
        nome: formData.nomeCliente.trim(),
        telefone: formData.contatoReserva?.trim() || undefined,
        clienteEventual: false,
      });
      if (novo.success && novo.data) {
        clienteId = novo.data.id;
        onClientesChanged?.();
      }
    } catch {
      // continua sem clienteId se falhar
    }
  }
  return clienteId;
}
